import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { capitalizeFirstName } from '@/utils/name-format'
import { Button } from '@/components/ui/button'
import { UpProjetsCell, LienProjetsDeLEntreprise } from '@/components/UpProjetsCell'
import { UpSpotBadge } from '@/components/UpSpotBadge'
import { UpSpotPanel } from '@/components/UpSpotPanel'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { useSettings } from '@/hooks/use-settings'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { 
  Search, 
  Filter,
  Plus,
  Settings,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Users,
  Phone,
  Mail,
  Building2,
  Contact as ContactIcon,
  SlidersHorizontal,
  ExternalLink,
  User,
  X,
  GitMerge,
} from 'lucide-react'
import { 
  apiService, 
  Contact, 
  Company, 
  ContactCreateInput, 
  ContactCreateInputWithCustomFields,
  ContactCustomValueInput,
  CustomField,
  CustomFieldOption,
} from '@/services/api'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/store/authStore'
import ChoixSuppressionUpSpot, { suppressionPossible, type ChoixCompteUpSpot } from '@/components/ChoixSuppressionUpSpot'
import FusionDialog from '@/components/FusionDialog'
import AlerteDoublon from '@/components/AlerteDoublon'

export default function Contacts() {
  const { toast } = useToast()
  const { settings, loading: settingsLoading } = useSettings()

  // États pour la gestion des données
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // États pour la recherche et pagination
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  
  // Utiliser les paramètres pour itemsPerPage
  const itemsPerPage = settings.elementsPerPageContacts
  
  // États pour le tri
  const [sortField, setSortField] = useState<string>('last_name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  
  // État pour les filtres
  const [showFilters, setShowFilters] = useState(false)
  const [advancedFilters, setAdvancedFilters] = useState<{
    field: string
    operator: string
    value: string
  }[]>([])
  const [filterLogicOperator, setFilterLogicOperator] = useState<'AND' | 'OR'>('AND')

  // États pour les filtres sauvegardés
  const [savedFilters, setSavedFilters] = useState<Array<{
    id: string
    name: string
    filters: { field: string; operator: string; value: string }[]
    logicOperator: 'AND' | 'OR'
    createdAt: string
  }>>([])
  const [isFilterSaveModalOpen, setIsFilterSaveModalOpen] = useState(false)
  const [filterSaveName, setFilterSaveName] = useState('')

  // États pour les champs de filtres
  const [availableFilterFields, setAvailableFilterFields] = useState<Array<{
    value: string
    label: string
    type: 'standard' | 'custom'
    fieldType?: string
    options?: Array<{ value: string; label: string }>
  }>>([])
  
  // Champs standards
  const standardFields = [
    { value: 'last_name', label: 'Nom', type: 'standard' as const, fieldType: 'text' },
    { value: 'first_name', label: 'Prénom', type: 'standard' as const, fieldType: 'text' },
    { value: 'email', label: 'Email', type: 'standard' as const, fieldType: 'email' },
    { value: 'phone', label: 'Téléphone', type: 'standard' as const, fieldType: 'text' },
    { value: 'company_name', label: 'Entreprise', type: 'standard' as const, fieldType: 'text' }
  ]

  // Fonction pour obtenir les opérateurs de filtre selon le type de champ
  const getFilterOperators = (fieldType?: string) => {
    switch (fieldType) {
      case 'number':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'date':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'between', label: 'Entre' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'radio':
      case 'select':
      case 'checkbox':
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
      case 'text':
      case 'email':
      case 'url':
      default:
        return [
          { value: 'equals', label: 'Est égal à' },
          { value: 'contains', label: 'Contient' },
          { value: 'starts_with', label: 'Commence par' },
          { value: 'ends_with', label: 'Se termine par' },
          { value: 'is_empty', label: 'Est vide' },
          { value: 'is_not_empty', label: "N'est pas vide" }
        ]
    }
  }

  // États pour la modale d'ajout/édition
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [contactToEdit, setContactToEdit] = useState<(Contact & { 
    company_name?: string
    custom_fields?: Array<{
      field: CustomField & { options?: CustomFieldOption[] }
      value?: string
    }>
  }) | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [companySearchTerm, setCompanySearchTerm] = useState('')

  // États pour la modal de confirmation de suppression
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null)
  // Sort du compte UP-SPOT porté par la fiche à supprimer (01/10/2026).
  const [choixUpSpot, setChoixUpSpot] = useState<ChoixCompteUpSpot>('desactiver')
  const [suppressionConfirmee, setSuppressionConfirmee] = useState(false)
  const estAdminCrm = Boolean(useAuthStore((etat) => etat.user?.is_admin))
  /* Filtre rapide « compte UP-SPOT » : le besoin de départ du 24/09/2026 était de
     voir, dans les contacts, qui est utilisateur d'UP-SPOT. */
  const [filtreUpSpot, setFiltreUpSpot] = useState<'tous' | 'avec' | 'sans'>('tous')
  const [isDeleting, setIsDeleting] = useState(false)

  // États pour les champs personnalisés
  const [customFields, setCustomFields] = useState<(CustomField & { usage_count: number, options?: CustomFieldOption[] })[]>([])
  const [customFieldValues, setCustomFieldValues] = useState<Record<number, string>>({})
  const [loadingCustomFields, setLoadingCustomFields] = useState(false)

  // États pour la gestion des colonnes visibles
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false)
  const [availableColumns, setAvailableColumns] = useState<Array<{
    id: string
    label: string
    type: 'standard' | 'custom'
    visible: boolean
  }>>([])
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])

  // États pour la modal de détails du contact
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  /* Le suivi UP-PROJETS de l'entreprise du contact ouvert (01/10/2026) :
     null tant qu'il n'est pas lu. */
  const [suiviEntreprise, setSuiviEntreprise] = useState<string[] | null>(null)
  const [selectedContact, setSelectedContact] = useState<(Contact & { 
    company_name?: string
    custom_field_values?: Record<number, string>
    custom_fields?: Array<{
      field: CustomField & { options?: CustomFieldOption[] }
      value?: string
    }>
  }) | null>(null)
  const [loadingContactDetails, setLoadingContactDetails] = useState(false)
  /* La fusion de deux contacts en double (28/10/2026) : la fiche de départ,
     et l'autre quand on la connaît déjà. */
  const [fusion, setFusion] = useState<{ ficheId: number; autreId?: number } | null>(null)

  // Colonnes standards par défaut
  const defaultColumns = [
    { id: 'last_name', label: 'Nom', type: 'standard' as const },
    { id: 'first_name', label: 'Prénom', type: 'standard' as const },
    { id: 'email', label: 'Email', type: 'standard' as const },
    { id: 'phone', label: 'Téléphone', type: 'standard' as const },
    { id: 'company_name', label: 'Entreprise', type: 'standard' as const }
  ]

  // État du formulaire
  const [formData, setFormData] = useState<ContactCreateInput>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    linkedin_url: '',
    company_id: undefined
  })

  // Fonctions pour gérer les filtres avancés
  const addAdvancedFilter = () => {
    const defaultField = availableFilterFields.length > 0 ? availableFilterFields[0].value : 'last_name'
    setAdvancedFilters(prev => [...prev, { field: defaultField, operator: 'contains', value: '' }])
  }

  const updateAdvancedFilter = (index: number, field: 'field' | 'operator' | 'value', value: string) => {
    setAdvancedFilters(prev => prev.map((filter, i) => {
      if (i === index) {
        const updatedFilter = { ...filter, [field]: value }
        
        // Si on change le champ, réinitialiser l'opérateur et la valeur
        if (field === 'field') {
          const selectedField = availableFilterFields.find(f => f.value === value)
          const operators = getFilterOperators(selectedField?.fieldType)
          updatedFilter.operator = operators.length > 0 ? operators[0].value : 'equals'
          updatedFilter.value = ''
        }
        
        // Si on change l'opérateur vers "is_empty" ou "is_not_empty", vider la valeur
        if (field === 'operator' && ['is_empty', 'is_not_empty'].includes(value)) {
          updatedFilter.value = ''
        }
        
        return updatedFilter
      }
      return filter
    }))
  }

  const removeAdvancedFilter = (index: number) => {
    setAdvancedFilters(prev => prev.filter((_, i) => i !== index))
  }

  const resetAdvancedFilters = () => {
    setAdvancedFilters([])
  }

  // Fonction pour rendre le champ de valeur selon le type de champ
  const renderValueField = (filter: { field: string; operator: string; value: string }, index: number) => {
    // Si l'opérateur ne nécessite pas de valeur
    if (['is_empty', 'is_not_empty'].includes(filter.operator)) {
      return (
        <div className="h-10 flex items-center px-3 py-2 border border-gray-200 bg-gray-50 rounded-md">
          <span className="text-sm text-gray-500">Aucune valeur requise</span>
        </div>
      )
    }

    // Trouver le champ sélectionné
    const selectedField = availableFilterFields.find(f => f.value === filter.field)
    const fieldType = selectedField?.fieldType

    switch (fieldType) {
      case 'number':
        return (
          <Input
            type="number"
            placeholder="Nombre"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'date':
        if (filter.operator === 'between') {
          const [startDate, endDate] = filter.value.split('|')
          return (
            <div className="flex space-x-2">
              <Input
                type="date"
                placeholder="Date de début"
                value={startDate || ''}
                onChange={(e) => {
                  const newValue = `${e.target.value}|${endDate || ''}`
                  updateAdvancedFilter(index, 'value', newValue)
                }}
              />
              <Input
                type="date"
                placeholder="Date de fin"
                value={endDate || ''}
                onChange={(e) => {
                  const newValue = `${startDate || ''}|${e.target.value}`
                  updateAdvancedFilter(index, 'value', newValue)
                }}
              />
            </div>
          )
        }
        return (
          <Input
            type="date"
            placeholder="Date"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'radio':
      case 'select':
        return (
          <Select 
            value={filter.value} 
            onValueChange={(value) => updateAdvancedFilter(index, 'value', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une option..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField?.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'checkbox':
        return (
          <Select 
            value={filter.value} 
            onValueChange={(value) => updateAdvancedFilter(index, 'value', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner une option..." />
            </SelectTrigger>
            <SelectContent>
              {selectedField?.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'email':
        return (
          <Input
            type="email"
            placeholder="Email"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'url':
        return (
          <Input
            type="url"
            placeholder="URL"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )

      case 'text':
      default:
        return (
          <Input
            placeholder="Valeur"
            value={filter.value}
            onChange={(e) => updateAdvancedFilter(index, 'value', e.target.value)}
          />
        )
    }
  }

  // Fonction pour gérer le tri des colonnes
  const handleSort = (columnId: string) => {
    if (sortField === columnId) {
      // Si on clique sur la même colonne, inverser l'ordre
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      // Si on clique sur une nouvelle colonne, trier par ordre croissant
      setSortField(columnId)
      setSortOrder('asc')
    }
    // Remettre à la première page lors d'un changement de tri
    setCurrentPage(1)
  }

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (columnId: string) => {
    if (sortField !== columnId) {
      return '↕' // Icône neutre
    }
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  // Fonctions pour gérer les filtres sauvegardés
  const loadSavedFilters = () => {
    try {
      const saved = localStorage.getItem('contacts_saved_filters')
      if (saved) {
        setSavedFilters(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Erreur lors du chargement des filtres sauvegardés:', error)
    }
  }

  const saveCurrentFilters = () => {
    if (!filterSaveName.trim()) return

    const newFilter = {
      id: Date.now().toString(),
      name: filterSaveName.trim(),
      filters: advancedFilters,
      logicOperator: filterLogicOperator,
      createdAt: new Date().toISOString()
    }

    const updatedFilters = [...savedFilters, newFilter]
    setSavedFilters(updatedFilters)
    localStorage.setItem('contacts_saved_filters', JSON.stringify(updatedFilters))
    
    setFilterSaveName('')
    setIsFilterSaveModalOpen(false)
    
    toast({
      variant: "success",
      title: "✅ Filtre sauvegardé !",
      description: `Le filtre "${newFilter.name}" a été sauvegardé avec succès.`
    })
  }

  // Fonctions pour la gestion des colonnes visibles
  const loadAvailableColumns = async () => {
    try {
      // Charger les champs personnalisés
      const response = await apiService.getContactCustomFields()
      
      let customColumns: Array<{id: string, label: string, type: 'custom'}> = []
      
      if (response.success && response.data) {
        customColumns = response.data.map(field => ({
          id: `custom_${field.id}`,
          label: field.name,
          type: 'custom' as const
        }))
      }
      
      // Combiner colonnes standard et personnalisées
      const allColumns = [...defaultColumns, ...customColumns]
      
      // Charger les préférences utilisateur depuis localStorage
      const savedColumns = localStorage.getItem('contacts_visible_columns')
      let visibleColumnIds: string[] = []
      
      if (savedColumns) {
        try {
          visibleColumnIds = JSON.parse(savedColumns)
        } catch {
          // En cas d'erreur, utiliser les colonnes par défaut
          visibleColumnIds = defaultColumns.slice(0, 5).map(col => col.id)
        }
      } else {
        // Première visite, utiliser les 5 premières colonnes par défaut
        visibleColumnIds = defaultColumns.slice(0, 5).map(col => col.id)
      }
      
      // Limiter à 5 colonnes maximum
      if (visibleColumnIds.length > 5) {
        visibleColumnIds = visibleColumnIds.slice(0, 5)
      }
      
      // Marquer les colonnes comme visibles ou non
      const columnsWithVisibility = allColumns.map(col => ({
        ...col,
        visible: visibleColumnIds.includes(col.id)
      }))
      
      setAvailableColumns(columnsWithVisibility)
      setSelectedColumns(visibleColumnIds)
      
    } catch (err) {
      console.error('Erreur lors du chargement des colonnes:', err)
      // En cas d'erreur, utiliser seulement les colonnes standards
      const standardWithVisibility = defaultColumns.map((col, index) => ({
        ...col,
        visible: index < 5
      }))
      setAvailableColumns(standardWithVisibility)
      setSelectedColumns(defaultColumns.slice(0, 5).map(col => col.id))
    }
  }

  const toggleColumn = (columnId: string) => {
    const newSelectedColumns = selectedColumns.includes(columnId)
      ? selectedColumns.filter(id => id !== columnId)
      : [...selectedColumns, columnId]
    
    // Limiter à 5 colonnes maximum
    if (newSelectedColumns.length <= 5) {
      setSelectedColumns(newSelectedColumns)
      
      // Mettre à jour l'état des colonnes disponibles
      setAvailableColumns(prev => prev.map(col => ({
        ...col,
        visible: newSelectedColumns.includes(col.id)
      })))
      
      // Sauvegarder dans localStorage
      localStorage.setItem('contacts_visible_columns', JSON.stringify(newSelectedColumns))
    }
  }

  const selectAllColumns = () => {
    const allColumnIds = availableColumns.map(col => col.id)
    const newSelectedColumns = allColumnIds.slice(0, 5) // Limiter à 5
    
    setSelectedColumns(newSelectedColumns)
    setAvailableColumns(prev => prev.map((col, index) => ({
      ...col,
      visible: index < 5
    })))
    
    localStorage.setItem('contacts_visible_columns', JSON.stringify(newSelectedColumns))
  }

  const deselectAllColumns = () => {
    setSelectedColumns([])
    setAvailableColumns(prev => prev.map(col => ({
      ...col,
      visible: false
    })))
    
    localStorage.setItem('contacts_visible_columns', JSON.stringify([]))
  }

  // Charger les contacts depuis l'API
  const loadContacts = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Préparer les filtres avancés pour l'API
      const filters = advancedFilters.filter(f => {
        // Pour les opérateurs "is_empty" et "is_not_empty", pas besoin de valeur
        if (['is_empty', 'is_not_empty'].includes(f.operator)) {
          return f.field && f.operator
        }
        // Pour les autres opérateurs, vérifier que la valeur n'est pas vide
        return f.field && f.operator && f.value.trim() !== ''
      })
      
      const response = await apiService.getContacts({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        sort: sortField,
        order: sortOrder,
        filters: filters.length > 0 ? {
          filters: filters,
          logicOperator: filterLogicOperator
        } : undefined,
        upspot: filtreUpSpot === 'tous' ? undefined : filtreUpSpot,
      })

      if (response.success && response.data) {
        setContacts(response.data.data)
        setTotalItems(response.data.total)
        setTotalPages(response.data.totalPages)
      } else {
        setError(response.error || 'Erreur lors du chargement des contacts')
      }
    } catch (err) {
      setError('Erreur de connexion')
      console.error('Erreur lors du chargement des contacts:', err)
    } finally {
      setLoading(false)
    }
  }

  // Charger la liste des entreprises pour le select
  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true)
      const response = await apiService.getCompanies({
        page: 1,
        limit: 100, // Récupérer beaucoup d'entreprises pour le select
        search: companySearchTerm || undefined,
        sort: 'name',
        order: 'asc'
      })

      if (response.success && response.data) {
        setCompanies(response.data.data)
      }
    } catch (err) {
      console.error('Erreur lors du chargement des entreprises:', err)
    } finally {
      setLoadingCompanies(false)
    }
  }

  // Charger les champs personnalisés pour les contacts
  const loadCustomFields = async (preserveValues = false) => {
    try {
      setLoadingCustomFields(true)
      const response = await apiService.getContactCustomFields()
      
      if (response.success && response.data) {
        setCustomFields(response.data)
        // Réinitialiser les valeurs des champs personnalisés seulement si pas en mode édition
        if (!preserveValues) {
          setCustomFieldValues({})
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des champs personnalisés:', err)
    } finally {
      setLoadingCustomFields(false)
    }
  }

  // Charger les champs disponibles pour les filtres (standards + personnalisés)
  const loadAvailableFilterFields = async () => {
    try {
      const response = await apiService.getContactCustomFields()
      
      let customFields: Array<{value: string, label: string, type: 'custom', fieldType: string, options?: Array<{ value: string; label: string }>}> = []
      
      if (response.success && response.data) {
        customFields = response.data.map(field => ({
          value: `custom_${field.id}`,
          label: field.name,
          type: 'custom' as const,
          fieldType: field.type,
          options: field.options?.filter(option => option.value !== undefined).map(option => ({
            value: option.value as string,
            label: option.label
          }))
        }))
      }
      
      // Combiner champs standards et personnalisés
      setAvailableFilterFields([...standardFields, ...customFields])
    } catch (err) {
      console.error('Erreur lors du chargement des champs de filtres:', err)
      // En cas d'erreur, utiliser seulement les champs standards
      setAvailableFilterFields(standardFields)
    }
  }

  // Charger les données au montage du composant et quand les paramètres changent
  useEffect(() => {
    // Attendre que les settings soient chargés
    if (settingsLoading) return
    loadContacts()
  }, [currentPage, searchTerm, advancedFilters, itemsPerPage, sortField, sortOrder, settingsLoading, filtreUpSpot])

  // Charger les champs de filtres au montage
  useEffect(() => {
    loadAvailableFilterFields()
    loadAvailableColumns()
    loadSavedFilters()
    loadCustomFields()
  }, [])

  // Charger les entreprises (filtrées par la recherche) quand la modale est ouverte
  useEffect(() => {
    if (isAddModalOpen) {
      loadCompanies()
    }
  }, [isAddModalOpen, companySearchTerm])

  // Charger les champs personnalisés à l'ouverture (sans écraser les valeurs en cours
  // de saisie quand on tape dans la recherche d'entreprise)
  useEffect(() => {
    if (isAddModalOpen) {
      // En mode édition, préserver les valeurs déjà définies
      loadCustomFields(isEditMode)
    }
  }, [isAddModalOpen, isEditMode])

  /* Le lien vers les projets de l'entreprise ne s'affiche que si elle a des
     projets suivis : on relit sa case « Suivi des actions » à l'ouverture. */
  const entrepriseDuContactOuvert = isDetailModalOpen ? selectedContact?.company_id : undefined
  useEffect(() => {
    setSuiviEntreprise(null)
    if (!entrepriseDuContactOuvert) return
    let actif = true
    apiService.getCompany(entrepriseDuContactOuvert).then((reponse) => {
      if (!actif) return
      const champs = (reponse.data as { custom_fields?: Array<{ field?: { name?: string }; value?: string }> } | undefined)?.custom_fields
      const valeur = champs?.find((c) => (c.field?.name || '').trim().toLowerCase() === 'suivi dans up-projets')?.value || ''
      setSuiviEntreprise(valeur.split(',').map((v) => v.trim()).filter(Boolean))
    }).catch(() => { if (actif) setSuiviEntreprise([]) })
    return () => { actif = false }
  }, [entrepriseDuContactOuvert])

  // Ouvrir automatiquement la fiche d'un contact ciblé via l'URL (?open=X)
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    /* ?modifier=X (01/10/2026) : ouvre directement la fenêtre « Modifier »,
       là où se gère l'accès UP-SPOT — on y arrive depuis la fiche entreprise. */
    const modifierId = parseInt(searchParams.get('modifier') || '', 10)
    if (!isNaN(modifierId)) {
      void handleEditContact({ id: modifierId } as Contact)
      searchParams.delete('modifier')
      setSearchParams(searchParams, { replace: true })
      return
    }
    const openId = searchParams.get('open')
    if (!openId) return
    const id = parseInt(openId, 10)
    if (isNaN(id)) return
    ;(async () => {
      try {
        const response = await apiService.getContact(id)
        if (response.success && response.data) {
          setSelectedContact(response.data as any)
          setIsDetailModalOpen(true)
        }
      } catch (e) {
        console.error('Erreur ouverture contact depuis URL:', e)
      } finally {
        // Nettoyer le paramètre pour éviter la réouverture au rafraîchissement
        searchParams.delete('open')
        setSearchParams(searchParams, { replace: true })
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Gérer les changements de page
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
  }

  // Gérer la recherche
  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
    setCurrentPage(1) // Reset à la première page lors d'une recherche
  }

  // Fonction pour formater le numéro de téléphone
  const formatPhoneNumber = (value: string): string => {
    // Supprimer tous les caractères non numériques
    const numbers = value.replace(/\D/g, '')
    
    // Limiter à 10 chiffres maximum
    const limitedNumbers = numbers.slice(0, 10)
    
    // Formater avec des espaces tous les 2 chiffres
    return limitedNumbers.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
  }

  // Fonction pour valider le numéro de téléphone
  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true // Le téléphone n'est pas obligatoire
    
    // Supprimer les espaces pour compter les chiffres
    const numbers = phone.replace(/\D/g, '')
    return numbers.length === 10
  }

  // Gérer les changements dans le formulaire
  const handleFormChange = (field: keyof ContactCreateInput, value: string | number | undefined) => {
    // Traitement spécial pour le téléphone
    if (field === 'phone' && typeof value === 'string') {
      const formattedPhone = formatPhoneNumber(value)
      setFormData(prev => ({
        ...prev,
        [field]: formattedPhone
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  // Gérer les changements des champs personnalisés
  const handleCustomFieldChange = (fieldId: number, value: string) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  // Soumettre le formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.first_name || !formData.last_name) {
      toast({
        variant: "destructive",
        title: "Erreur de validation",
        description: "Le prénom et le nom sont requis"
      })
      return
    }

    if (!formData.email) {
      toast({
        variant: "destructive",
        title: "Erreur de validation", 
        description: "L'email est requis"
      })
      return
    }

    // Validation du numéro de téléphone
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      toast({
        variant: "destructive",
        title: "Erreur de validation",
        description: "Le numéro de téléphone doit contenir exactement 10 chiffres"
      })
      return
    }

    try {
      setIsSubmitting(true)
      
      // Préparer les données avec les champs personnalisés
      const customFieldsData: ContactCustomValueInput[] = Object.entries(customFieldValues)
        .filter(([_fieldId, value]) => value && value.trim() !== '')
        .map(([fieldId, value]) => ({
          contacts_custom_field_id: parseInt(fieldId),
          value: value.trim()
        }))

      const contactData: ContactCreateInputWithCustomFields = {
        ...formData,
        custom_fields: customFieldsData.length > 0 ? customFieldsData : undefined
      }

      let response

      if (isEditMode && contactToEdit) {
        // Mode édition : mettre à jour le contact existant
        response = await apiService.updateContact(contactToEdit.id, contactData)
      } else {
        // Mode création : créer un nouveau contact
        response = await apiService.createContact(contactData)
      }
      
      if (response.success) {
        // Fermer la modale et réinitialiser
        handleCloseModal()
        
        // Recharger la liste des contacts
        await loadContacts()

        // La fiche est enregistrée, mais son compte UP-SPOT n'a pas pu suivre.
        if (response.avertissement) {
          toast({ variant: "destructive", title: "Compte UP-SPOT non mis à jour", description: response.avertissement })
        }
        // La fiche est enregistrée, mais le registre des visiteurs n'a pas pu suivre.
        if (response.avertissementUpRegistre) {
          toast({ variant: "destructive", title: "Registre des visiteurs non mis à jour", description: response.avertissementUpRegistre })
        }
        
        toast({
          variant: "success",
          title: isEditMode ? "✅ Contact modifié !" : "✅ Contact créé !",
          description: isEditMode 
            ? `${formData.first_name} ${formData.last_name} a été modifié avec succès`
            : `${formData.first_name} ${formData.last_name} a été ajouté avec succès`
        })
              } else {
          toast({
            variant: "destructive",
          title: isEditMode ? "Erreur de modification" : "Erreur de création",
          description: response.error || "Une erreur s'est produite lors de l'opération"
          })
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Erreur de connexion",
        description: isEditMode 
          ? "Impossible de modifier le contact. Vérifiez votre connexion."
          : "Impossible de créer le contact. Vérifiez votre connexion."
        })
        console.error('Erreur:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Ouvrir la modal de confirmation de suppression
  const handleDeleteContact = async (contact: Contact) => {
    setContactToDelete(contact)
    setChoixUpSpot('desactiver')
    setSuppressionConfirmee(false)
    setIsDeleteModalOpen(true)
  }

  // Confirmer la suppression du contact
  const confirmDeleteContact = async () => {
    if (!contactToDelete) return

    try {
      setIsDeleting(true)
      const toutSupprimer = Boolean(contactToDelete.upspot_compte_id) && choixUpSpot === 'supprimer'
      const response = await apiService.deleteContact(
        contactToDelete.id,
        toutSupprimer ? { compteUpSpot: 'supprimer' } : {},
      )
        if (response.success) {
          await loadContacts() // Recharger la liste
        setIsDeleteModalOpen(false)
        setContactToDelete(null)
          toast({
            variant: "success",
            title: "✅ Contact mis à la corbeille",
            description: toutSupprimer
              ? "Le contact est dans la corbeille ; son compte UP-SPOT et ses réservations, eux, sont supprimés définitivement."
              : contactToDelete.upspot_compte_id
                ? "Le contact est dans la corbeille et son compte UP-SPOT est désactivé."
                : "Un administrateur peut le restaurer depuis la corbeille."
          })
        } else {
          toast({
            variant: "destructive",
            title: "Erreur de suppression",
            description: response.error || "Impossible de supprimer le contact"
          })
        }
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Erreur de connexion",
          description: "Impossible de supprimer le contact. Vérifiez votre connexion."
        })
    } finally {
      setIsDeleting(false)
    }
  }

  // Annuler la suppression
  const cancelDeleteContact = () => {
    setIsDeleteModalOpen(false)
    setContactToDelete(null)
  }

  // Fonction pour rendre un champ personnalisé selon son type
  const renderCustomField = (field: CustomField & { options?: CustomFieldOption[] }) => {
    const value = customFieldValues[field.id] || ''
    const isRequired = field.required === 1

    switch (field.type) {
      case 'text':
        return (
          <Input
            key={field.id}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )

      case 'number':
        return (
          <Input
            key={field.id}
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )

      case 'email':
        return (
          <Input
            key={field.id}
            type="email"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`email@exemple.com`}
            required={isRequired}
          />
        )

      case 'url':
        return (
          <Input
            key={field.id}
            type="url"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`https://exemple.com`}
            required={isRequired}
          />
        )

      case 'date':
        return (
          <Input
            key={field.id}
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            required={isRequired}
          />
        )

      case 'select':
        return (
          <Select
            key={field.id}
            value={value}
            onValueChange={(selectedValue) => handleCustomFieldChange(field.id, selectedValue)}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Sélectionner ${field.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.id} value={option.value || option.label}>
                  <div className="flex items-center space-x-2">
                    {field.color === 1 && option.color && (
                      <div
                        className="w-3 h-3 rounded-full border"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )

      case 'radio':
        return (
          <div
            key={field.id}
            className="inline-flex p-0.5 rounded-lg bg-gray-100 text-sm font-medium"
          >
            {field.options?.map((option) => {
              const optionValue = option.value || option.label
              const selected = value === optionValue
              const lower = option.label.trim().toLowerCase()
              const selectedClasses =
                lower === 'oui'
                  ? 'bg-emerald-600 text-white'
                  : lower === 'non'
                    ? 'bg-rose-500 text-white'
                    : 'bg-blue-600 text-white'
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleCustomFieldChange(field.id, optionValue)}
                  className={`px-3 py-1 rounded-md transition-colors ${
                    selected
                      ? selectedClasses
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        )

      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            {field.options?.map((option) => {
              const selectedValues = value ? value.split(',') : []
              const isChecked = selectedValues.includes(option.value || option.label)
              
              return (
                <div key={option.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const optionValue = option.value || option.label
                      let newValues = [...selectedValues]
                      
                      if (e.target.checked) {
                        newValues.push(optionValue)
                      } else {
                        newValues = newValues.filter(v => v !== optionValue)
                      }
                      
                      handleCustomFieldChange(field.id, newValues.join(','))
                    }}
                    className="w-4 h-4 text-blue-600"
                  />
                  {field.color === 1 && option.color && (
                    <div
                      className="w-3 h-3 rounded-full border"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                  <Label className="text-sm font-normal cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              )
            })}
          </div>
        )

      default:
        return (
          <Input
            key={field.id}
            value={value}
            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
            placeholder={`Entrez ${field.name.toLowerCase()}`}
            required={isRequired}
          />
        )
    }
  }

  // Fonction pour obtenir la valeur d'une colonne pour un contact
  const getColumnValue = (contact: Contact & { company_name?: string, custom_field_values?: Record<number, string> }, columnId: string) => {
    if (columnId.startsWith('custom_')) {
      // Pour les champs personnalisés, extraire l'ID et chercher dans les valeurs
      const fieldId = parseInt(columnId.replace('custom_', ''))
      const customFieldValues = (contact as any).custom_field_values || {}
      return customFieldValues[fieldId] || '-'
    }
    
    switch (columnId) {
      case 'last_name':
        return contact.last_name
      case 'first_name':
        return contact.first_name
      case 'email':
        return contact.email || '-'
      case 'phone':
        return contact.phone || '-'
      case 'company_name':
        return (contact as any).company_name || '-'
      default:
        return '-'
    }
  }

  // Fonction pour rendre une cellule de tableau
  const renderTableCell = (contact: Contact & { company_name?: string, custom_field_values?: Record<number, string> }, columnId: string) => {
    const value = getColumnValue(contact, columnId)
    
    switch (columnId) {
      case 'last_name':
        return (
          <div className="flex items-center space-x-2">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="font-medium">{value}</span>
          </div>
        )
      case 'email':
        return value !== '-' ? (
          <div className="flex items-center space-x-1">
            <Mail className="w-3 h-3 text-gray-400" />
            <a 
              href={`mailto:${value}`} 
              className="text-blue-600 hover:underline"
            >
              {value}
            </a>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      case 'phone':
        return value !== '-' ? (
          <div className="flex items-center space-x-1">
            <Phone className="w-3 h-3 text-gray-400" />
            <span>{value}</span>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      case 'company_name':
        return value !== '-' ? (
          <div className="flex items-center space-x-1">
            <Building2 className="w-3 h-3 text-gray-400" />
            <span>{value}</span>
          </div>
        ) : (
          <span className="text-gray-500">-</span>
        )
      default:
        return <span>{value}</span>
    }
  }

  // Fonction pour obtenir le label d'une colonne
  const getColumnLabel = (columnId: string) => {
    const column = availableColumns.find(col => col.id === columnId)
    return column ? column.label : columnId
  }

  // Fonction pour charger les détails complets d'un contact
  const loadContactDetails = async (contactId: number) => {
    setLoadingContactDetails(true)
    try {
      const response = await apiService.getContact(contactId)
      if (response.success && response.data) {
        // Mettre à jour le contact sélectionné avec les détails complets
        setSelectedContact(response.data)
      } else {
        toast({
          variant: "default",
          title: "❌ Erreur",
          description: "Impossible de charger les détails du contact."
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error)
      toast({
        variant: "default",
        title: "❌ Erreur",
        description: "Une erreur est survenue lors du chargement des détails."
      })
    } finally {
      setLoadingContactDetails(false)
    }
  }

  // Fonction pour ouvrir la modal de détails
  const handleViewContact = async (contact: Contact & { company_name?: string, custom_field_values?: Record<number, string> }) => {
    // Ouvrir la modal immédiatement avec les données de base
    setSelectedContact(contact)
    setIsDetailModalOpen(true)
    
    // Charger les détails complets en arrière-plan
    await loadContactDetails(contact.id)
  }

  // Ouvrir un contact connu par son seul numéro (depuis l'alerte de doublon).
  const ouvrirContact = async (contactId: number) => {
    setSelectedContact(null)
    setIsDetailModalOpen(true)
    await loadContactDetails(contactId)
  }

  // Fonction pour ouvrir la modal en mode édition
  const handleEditContact = async (contact: Contact & { company_name?: string, custom_field_values?: Record<number, string> }) => {
    try {
      // Charger les détails complets du contact avec les champs personnalisés
      const response = await apiService.getContact(contact.id)
      
      if (response.success && response.data) {
        const contactDetails = response.data
        
        // Précharger les données de base dans le formulaire
        setFormData({
          first_name: contactDetails.first_name,
          last_name: contactDetails.last_name,
          email: contactDetails.email || '',
          phone: contactDetails.phone ? formatPhoneNumber(contactDetails.phone) : '',
          linkedin_url: contactDetails.linkedin_url || '',
          company_id: contactDetails.company_id || undefined
        })
        
        // Précharger les valeurs des champs personnalisés
        const customFieldsValues: Record<number, string> = {}
        
        // Vérifier la structure custom_fields (API détaillée)
        const customFieldsData = (contactDetails as any).custom_fields
        if (customFieldsData && Array.isArray(customFieldsData)) {
          for (let i = 0; i < customFieldsData.length; i++) {
            const fieldData = customFieldsData[i]
            if (fieldData.value && fieldData.field && fieldData.field.id) {
              customFieldsValues[fieldData.field.id] = fieldData.value
            }
          }
        }
        
        // Définir le contact à éditer et passer en mode édition
        setContactToEdit(contactDetails)
        setIsEditMode(true)
        
        // Définir les valeurs des champs personnalisés
        setCustomFieldValues(customFieldsValues)
        
        // Ouvrir la modal (l'useEffect chargera les champs mais préservera les valeurs)
        setIsAddModalOpen(true)
      } else {
        toast({
          variant: "destructive",
          title: "❌ Erreur",
          description: "Impossible de charger les détails du contact pour l'édition."
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement pour l\'édition:', error)
      toast({
        variant: "destructive",
        title: "❌ Erreur",
        description: "Une erreur est survenue lors du chargement pour l'édition."
      })
    }
  }

  // Fonction pour ouvrir la modal en mode création (force la réinitialisation)
  const handleOpenAddModal = () => {
    // Forcer la réinitialisation de tous les états
    setIsEditMode(false)
    setContactToEdit(null)
    
    // Réinitialiser le formulaire
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      linkedin_url: '',
      company_id: undefined
    })
    
    // Réinitialiser les champs personnalisés
    setCustomFieldValues({})

    // Ouvrir la modal
    setIsAddModalOpen(true)
  }

  // Fonction pour fermer la modal et réinitialiser les états
  const handleCloseModal = () => {
    setIsAddModalOpen(false)
    setIsEditMode(false)
    setContactToEdit(null)
    
    // Réinitialiser le formulaire
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      linkedin_url: '',
      company_id: undefined
    })
    
    // Réinitialiser les champs personnalisés
    setCustomFieldValues({})
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <Users className="w-6 h-6 text-blue-600" />
            <span>Contacts</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos contacts clients et prospects
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Dialog open={isColumnsModalOpen} onOpenChange={setIsColumnsModalOpen}>
            <DialogTrigger asChild>
          <Button variant="outline" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Colonnes visibles</span>
          </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] rounded-2xl sm:rounded-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center space-x-2">
                  <Settings className="w-5 h-5" />
                  <span>Gérer les colonnes</span>
                </DialogTitle>
                <DialogDescription>
                  Choisissez les colonnes à afficher dans le tableau (maximum 5 colonnes pour un affichage optimal)
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Boutons d'action rapide */}
                <div className="flex space-x-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllColumns}
                    className="text-blue-600"
                    disabled={selectedColumns.length === 5}
                  >
                    Tout sélectionner
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={deselectAllColumns}
                    className="text-gray-600"
                    disabled={selectedColumns.length === 0}
                  >
                    Tout désélectionner
                  </Button>
                </div>

                {/* Indicateur de limite */}
                <div className="text-sm text-gray-500">
                  {selectedColumns.length}/5 colonnes sélectionnées
                </div>

                {/* Liste des colonnes */}
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {/* Colonnes standards */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Champs standards</h4>
                    <div className="space-y-2">
                      {availableColumns
                        .filter(col => col.type === 'standard')
                        .map((column) => (
                          <div key={column.id} className="flex items-center justify-between">
                            <span className="text-sm">{column.label}</span>
                            <Button
                              variant={column.visible ? "default" : "outline"}
                              size="sm"
                              className={`w-12 h-8 ${column.visible ? 'bg-blue-600' : ''}`}
                              onClick={() => toggleColumn(column.id)}
                              disabled={!column.visible && selectedColumns.length >= 5}
                            >
                              {column.visible ? '✓' : ''}
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Colonnes personnalisées */}
                  {availableColumns.some(col => col.type === 'custom') && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        Champs personnalisés
                        <Badge variant="secondary" className="ml-2 text-xs">
                          Personnalisé
                        </Badge>
                      </h4>
                      <div className="space-y-2">
                        {availableColumns
                          .filter(col => col.type === 'custom')
                          .map((column) => (
                            <div key={column.id} className="flex items-center justify-between">
                              <span className="text-sm">{column.label}</span>
                              <Button
                                variant={column.visible ? "default" : "outline"}
                                size="sm"
                                className={`w-12 h-8 ${column.visible ? 'bg-blue-600' : ''}`}
                                onClick={() => toggleColumn(column.id)}
                                disabled={!column.visible && selectedColumns.length >= 5}
                              >
                                {column.visible ? '✓' : ''}
                              </Button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setIsColumnsModalOpen(false)}>
                  Terminé
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Bouton d'ajout avec modale */}
          <Dialog open={isAddModalOpen} onOpenChange={(open) => {
            if (!open) {
              handleCloseModal()
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700"
                onClick={handleOpenAddModal}
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un contact</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col rounded-2xl sm:rounded-2xl">
              <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <DialogTitle className="text-xl font-semibold">
                      {isEditMode ? 'Modifier le contact' : 'Ajouter un contact'}
                    </DialogTitle>
                    <DialogDescription>
                      {isEditMode
                        ? 'Modifiez les informations de ce contact'
                        : 'Créez un nouveau contact dans votre CRM'}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto px-6 py-2 space-y-6">
                {/* Section Coordonnées */}
                <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  <ContactIcon className="w-4 h-4" /> Coordonnées
                </h3>
                {/* Première ligne : Prénom et Nom */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom *</Label>
                    <div className="relative">
                      <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) =>
                          handleFormChange(
                            'first_name',
                            capitalizeFirstName(e.target.value),
                          )
                        }
                        placeholder="Entrez le prénom"
                        required
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) =>
                        handleFormChange('last_name', e.target.value.toUpperCase())
                      }
                      placeholder="Entrez le nom"
                      required
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                </div>

                {/* Deuxième ligne : Email et Téléphone */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        placeholder="contact@exemple.com"
                        required
                        className="pl-9"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleFormChange('phone', e.target.value)}
                        placeholder="01 23 45 67 89"
                        className={`pl-9 ${formData.phone && !validatePhoneNumber(formData.phone) ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {formData.phone && !validatePhoneNumber(formData.phone) && (
                      <p className="text-red-500 text-xs mt-1">
                        Le numéro doit contenir exactement 10 chiffres
                      </p>
                    )}
                    {formData.phone && validatePhoneNumber(formData.phone) && (
                      <p className="text-green-600 text-xs mt-1">
                        ✓ Format valide
                      </p>
                    )}
                  </div>
                </div>

                <AlerteDoublon
                  type="contact"
                  email={formData.email}
                  sauf={isEditMode ? contactToEdit?.id : undefined}
                  onOuvrir={(id) => { handleCloseModal(); void ouvrirContact(id) }}
                  onFusionner={isEditMode && contactToEdit
                    ? (id) => { const garde = contactToEdit.id; handleCloseModal(); setFusion({ ficheId: garde, autreId: id }) }
                    : undefined}
                />

                {/* URL LinkedIn (optionnel) */}
                <div className="space-y-2">
                  <Label htmlFor="linkedin_url">URL LinkedIn</Label>
                  <div className="relative">
                    <svg viewBox="0 0 24 24" fill="#0A66C2" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
                    <Input
                      id="linkedin_url"
                      type="url"
                      value={formData.linkedin_url || ''}
                      onChange={(e) => handleFormChange('linkedin_url', e.target.value)}
                      placeholder="https://linkedin.com/in/prenom-nom"
                      className="pl-9"
                    />
                  </div>
                </div>

                {/* Troisième ligne : Entreprise */}
                <div className="space-y-2">
                  <Label htmlFor="company">Entreprise</Label>
                  <Select 
                    value={formData.company_id?.toString() || ''} 
                    onValueChange={(value) => handleFormChange('company_id', value ? parseInt(value) : undefined)}
                  >
                    <SelectTrigger>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                        <SelectValue placeholder="Sélectionner une entreprise" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <div className="p-2">
                        <Input
                          placeholder="Rechercher une entreprise..."
                          value={companySearchTerm}
                          onChange={(e) => setCompanySearchTerm(e.target.value)}
                          className="mb-2"
                        />
                      </div>
                      {loadingCompanies ? (
                        <SelectItem value="loading" disabled>
                          Chargement...
                        </SelectItem>
                      ) : companies.length === 0 ? (
                        <SelectItem value="no-companies" disabled>
                          Aucune entreprise trouvée
                        </SelectItem>
                      ) : (
                        companies.map((company) => (
                          <SelectItem key={company.id} value={company.id.toString()}>
                            <div className="flex items-center space-x-2">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span>{company.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                </div>

                {/* Section Champs personnalisés */}
                {customFields.length > 0 && (
                  <div className="border-t border-gray-200 pt-6 space-y-4">
                    <h3 className="flex items-center gap-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                      <SlidersHorizontal className="w-4 h-4" /> Champs personnalisés
                    </h3>
                    {loadingCustomFields ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        <span className="ml-2 text-sm text-gray-500">Chargement...</span>
                      </div>
                    ) : (
                      <div className="rounded-xl ring-1 ring-gray-200 divide-y divide-gray-100 overflow-hidden">
                        {customFields.map((field) => (
                          <div
                            key={field.id}
                            className="flex items-center justify-between gap-4 px-4 py-3"
                          >
                            <Label htmlFor={`custom_field_${field.id}`} className="text-sm font-medium text-gray-700">
                              {field.name}
                              {field.required === 1 && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            <div className="shrink-0 w-48 flex justify-end">
                              {renderCustomField(field)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Accès UP-SPOT (01/10/2026) : géré ici, dans une section à part
                    qui s'enregistre avec son propre bouton, indépendamment du
                    bouton « Modifier le contact ». */}
                {isEditMode && contactToEdit && (
                  <UpSpotPanel
                    gestion
                    contactId={contactToEdit.id}
                    email={contactToEdit.email}
                    onChange={() => void loadContacts()}
                  />
                )}
                </div>

                {/* Boutons d'action */}
                <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white">
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCloseModal}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {isSubmitting
                        ? isEditMode
                          ? 'Modification...'
                          : 'Création...'
                        : isEditMode
                          ? 'Modifier le contact'
                          : 'Créer le contact'}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Rechercher un contact..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Filtres avancés */}
      <div className="space-y-4">
      <div className="flex items-center space-x-4">
          <Button 
            variant="outline" 
            className="flex items-center space-x-2"
            onClick={() => setShowFilters(!showFilters)}
          >
              <Filter className="w-4 h-4" />
              <span>Filtres avancés</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
        
        <select
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          value={filtreUpSpot}
          onChange={(e) => { setFiltreUpSpot(e.target.value as 'tous' | 'avec' | 'sans'); setCurrentPage(1) }}
          title="Filtrer selon le compte UP-SPOT"
        >
          <option value="tous">UP-SPOT : tous les contacts</option>
          <option value="avec">UP-SPOT : avec un compte</option>
          <option value="sans">UP-SPOT : sans compte</option>
        </select>

        {/* Indicateur de résultats */}
        <div className="text-sm text-gray-600">
          {loading ? 'Chargement...' : `${totalItems} contact${totalItems > 1 ? 's' : ''} trouvé${totalItems > 1 ? 's' : ''}`}
        </div>
        </div>

        {/* Section des filtres avancés */}
        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="space-y-3">
              {advancedFilters.map((filter, index) => (
                <div key={index} className="grid grid-cols-12 gap-3 items-end">
                  {/* Nom */}
                  <div className="col-span-3">
                    <Label className="text-sm font-medium text-gray-700">Nom</Label>
                    <Select 
                      value={filter.field} 
                      onValueChange={(value) => updateAdvancedFilter(index, 'field', value)}
                    >
                                             <SelectTrigger>
                         <SelectValue placeholder="Sélectionner..." />
                       </SelectTrigger>
                       <SelectContent>
                         {/* Champs standards */}
                         <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100">
                           Champs standards
                         </div>
                         {availableFilterFields
                           .filter(field => field.type === 'standard')
                           .map((field) => (
                             <SelectItem key={field.value} value={field.value}>
                               {field.label}
                             </SelectItem>
                           ))}
                         
                         {/* Champs personnalisés */}
                         {availableFilterFields.some(field => field.type === 'custom') && (
                           <>
                             <div className="px-2 py-1.5 text-sm font-medium text-gray-500 bg-gray-100 mt-1">
                               Champs personnalisés
                             </div>
                             {availableFilterFields
                               .filter(field => field.type === 'custom')
                               .map((field) => (
                                 <SelectItem key={field.value} value={field.value}>
                                   {field.label}
                                 </SelectItem>
                               ))}
                           </>
                         )}
                       </SelectContent>
                    </Select>
                  </div>

                  {/* Est égal à */}
                  <div className="col-span-3">
                    <Label className="text-sm font-medium text-gray-700">Opérateur</Label>
                    <Select 
                      value={filter.operator} 
                      onValueChange={(value) => updateAdvancedFilter(index, 'operator', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selectedField = availableFilterFields.find(f => f.value === filter.field)
                          const operators = getFilterOperators(selectedField?.fieldType)
                          return operators.map((operator) => (
                            <SelectItem key={operator.value} value={operator.value}>
                              {operator.label}
                            </SelectItem>
                          ))
                        })()}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Valeur */}
                  <div className="col-span-5">
                    <Label className="text-sm font-medium text-gray-700">Valeur</Label>
                    {renderValueField(filter, index)}
                  </div>

                  {/* Bouton supprimer */}
                  <div className="col-span-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAdvancedFilter(index)}
                      className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

                          {/* Opérateur logique entre les filtres */}
            {advancedFilters.length > 1 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Opérateur logique entre les filtres
                </Label>
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="filter-and"
                      name="filterLogic"
                      value="AND"
                      checked={filterLogicOperator === 'AND'}
                      onChange={(e) => setFilterLogicOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="filter-and" className="text-sm text-gray-700">
                      <span className="font-medium text-blue-600">ET</span> (tous les filtres doivent correspondre)
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      id="filter-or"
                      name="filterLogic"
                      value="OR"
                      checked={filterLogicOperator === 'OR'}
                      onChange={(e) => setFilterLogicOperator(e.target.value as 'AND' | 'OR')}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="filter-or" className="text-sm text-gray-700">
                      <span className="font-medium text-blue-600">OU</span> (au moins un filtre doit correspondre)
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Message si aucun filtre */}
            {advancedFilters.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                Aucun filtre défini. Utilisez le filtre rapide ci-dessus ou cliquez sur "Ajouter un filtre" pour commencer.
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-3">
              <Button
                onClick={addAdvancedFilter}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter un filtre</span>
              </Button>
              <Button
                onClick={() => {
                  setAdvancedFilters([])
                  setCurrentPage(1)
                }}
                variant="outline"
                disabled={advancedFilters.length === 0}
              >
                Appliquer les filtres
              </Button>
              <Button
                variant="outline"
                onClick={resetAdvancedFilters}
                disabled={advancedFilters.length === 0}
              >
                Réinitialiser
              </Button>
            </div>

            {/* Bouton enregistrer les filtres */}
            {advancedFilters.length > 0 && (
              <Dialog open={isFilterSaveModalOpen} onOpenChange={setIsFilterSaveModalOpen}>
                <DialogContent className="sm:max-w-[400px] rounded-2xl sm:rounded-2xl">
                  <DialogHeader>
                    <DialogTitle>Enregistrer le filtre</DialogTitle>
                    <DialogDescription>
                      Donnez un nom à ce filtre pour le retrouver facilement plus tard.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="filter-name">Nom du filtre</Label>
                      <Input
                        id="filter-name"
                        placeholder="Ex: Contacts récents de Paris"
                        value={filterSaveName}
                        onChange={(e) => setFilterSaveName(e.target.value)}
                      />
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      <strong>Aperçu:</strong> {advancedFilters.length} filtre(s) avec opérateur {filterLogicOperator}
                    </div>
                  </div>

                  <DialogFooter>
                    <Button 
                      variant="outline" 
                      onClick={() => setIsFilterSaveModalOpen(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      onClick={saveCurrentFilters}
                      disabled={!filterSaveName.trim()}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Enregistrer
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          </div>
        )}
      </div>

      {/* Gestion des erreurs */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <p>❌ {error}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadContacts}
            className="mt-2"
          >
            Réessayer
          </Button>
        </div>
      )}

      {/* Tableau */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              {selectedColumns.map((columnId) => (
                <TableHead 
                  key={columnId} 
                  className="font-medium cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort(columnId)}
                  title={`Trier par ${getColumnLabel(columnId)}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{getColumnLabel(columnId).toUpperCase()}</span>
                    <span className="ml-1 text-gray-400">{getSortIcon(columnId)}</span>
                  </div>
                </TableHead>
              ))}
              {/* La colonne qui relie les deux applications. Sur un contact, le
                  compte rendu part avec son interlocuteur déjà renseigné. */}
              <TableHead className="font-medium">UP-PROJETS</TableHead>
              <TableHead className="font-medium">UP-SPOT</TableHead>
              <TableHead className="font-medium">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              // Squelettes de chargement
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {selectedColumns.map((columnId) => (
                    <TableCell key={columnId}>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                  ))}
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                  </TableCell>
                </TableRow>
              ))
            ) : contacts.length === 0 ? (
              // Aucun contact trouvé
              <TableRow>
                <TableCell colSpan={selectedColumns.length + 3} className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun contact trouvé</p>
                  {searchTerm && (
                    <p className="text-sm mt-2">
                      Essayez de modifier vos critères de recherche
                    </p>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              // Liste des contacts
              contacts.map((contact) => (
                <TableRow key={contact.id} className="hover:bg-gray-50">
                  {selectedColumns.map((columnId) => (
                    <TableCell
                      key={columnId}
                      className={`cursor-pointer ${columnId === 'last_name' ? 'font-medium' : ''}`}
                      onClick={() => handleViewContact(contact)}
                    >
                      {renderTableCell(contact, columnId)}
                  </TableCell>
                  ))}
                  <TableCell>
                    <UpProjetsCell
                      companyId={contact.company_id}
                      nom={contact.company_name}
                      /* Le statut n'est pas chargé sur cette page : on n'en
                         affiche aucun plutôt que d'en affirmer un faux. */
                    />
                  </TableCell>
                  <TableCell className="cursor-pointer" onClick={() => handleViewContact(contact)}>
                    <UpSpotBadge
                      compteId={contact.upspot_compte_id}
                      role={contact.upspot_role}
                      statut={contact.upspot_statut}
                      invitationEnAttente={contact.upspot_invitation_en_attente}
                    />
                  </TableCell>
                  <TableCell>
                      <div className="flex items-center space-x-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Modifier"
                        onClick={(e) => { e.stopPropagation(); handleEditContact(contact); }}
                      >
                        <Edit className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Supprimer"
                        onClick={(e) => { e.stopPropagation(); handleDeleteContact(contact); }}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination améliorée */}
      {!loading && contacts.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-700">
              Affichage de {((currentPage - 1) * itemsPerPage) + 1} à{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} sur {totalItems} résultats
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Navigation directe */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Aller à la page :</span>
              <Input
                type="number"
                min="1"
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value)
                  if (page >= 1 && page <= totalPages) {
                    handlePageChange(page)
                  }
                }}
                className="w-16 h-8 text-center"
              />
              <span className="text-sm text-gray-600">sur {totalPages}</span>
            </div>

            {/* Boutons de pagination */}
            <div className="flex items-center space-x-1">
              {/* Première page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(1)}
                disabled={currentPage <= 1}
                className="w-8 h-8 p-0"
                title="Première page"
              >
                ««
              </Button>

              {/* Page précédente */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="flex items-center space-x-1 px-3"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Précédent</span>
              </Button>
              
              {/* Pages numérotées intelligentes */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pages = []
                  const showPages = 5 // Nombre de pages à afficher
                  let startPage = Math.max(1, currentPage - Math.floor(showPages / 2))
                  const endPage = Math.min(totalPages, startPage + showPages - 1)
                  
                  // Ajuster si on est près de la fin
                  if (endPage - startPage < showPages - 1) {
                    startPage = Math.max(1, endPage - showPages + 1)
                  }

                  // Première page + ellipsis si nécessaire
                  if (startPage > 1) {
                    pages.push(
                      <Button
                        key={1}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="w-8 h-8 p-0"
                      >
                        1
                      </Button>
                    )
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-2 text-gray-400">...</span>
                      )
                    }
                  }

                  // Pages du milieu
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={currentPage === i ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    )
                  }

                  // Ellipsis + dernière page si nécessaire
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-2 text-gray-400">...</span>
                      )
                    }
                    pages.push(
                      <Button
                        key={totalPages}
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="w-8 h-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    )
                  }

                  return pages
                })()}
              </div>
              
              {/* Page suivante */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="flex items-center space-x-1 px-3"
              >
                <span className="hidden sm:inline">Suivant</span>
                <ChevronRight className="w-4 h-4" />
              </Button>

              {/* Dernière page */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
                className="w-8 h-8 p-0"
                title="Dernière page"
              >
                »»
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de détails du contact */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl sm:rounded-2xl">
          {selectedContact && (
            <div className="max-h-[85vh] overflow-y-auto">
              {/* En-tête sobre avec avatar dégradé */}
              <div className="flex items-start justify-between px-6 py-5 border-b border-slate-100">
                <div className="flex items-center gap-4 min-w-0 pr-2">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg font-bold shadow-md shrink-0">
                    {`${selectedContact.first_name?.[0] ?? ''}${selectedContact.last_name?.[0] ?? ''}`.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <DialogTitle className="text-xl font-bold text-slate-800 leading-tight truncate">
                      {selectedContact.first_name} {selectedContact.last_name}
                    </DialogTitle>
                    <p className="text-sm font-medium text-blue-600 mt-0.5 truncate">
                      {selectedContact.company_name || 'Aucune entreprise'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center shrink-0 pr-8">
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center gap-2 font-semibold"
                    onClick={async () => {
                      setIsDetailModalOpen(false)
                      await handleEditContact(selectedContact)
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    <span>Modifier</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="inline-flex items-center gap-2 font-semibold ml-2"
                    title="Fusionner avec un contact en double"
                    onClick={() => {
                      setIsDetailModalOpen(false)
                      setFusion({ ficheId: selectedContact.id })
                    }}
                  >
                    <GitMerge className="w-4 h-4" />
                    <span>Fusionner</span>
                  </Button>
                </div>
              </div>

              {/* Zones colorées par catégorie */}
              <div className="p-6 space-y-4">
                {/* Zone Coordonnées (bleu) */}
                <div className="rounded-2xl border border-blue-100 bg-blue-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-100/70 border-b border-blue-100">
                    <ContactIcon className="w-4 h-4 text-blue-700" />
                    <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide">Coordonnées</h3>
                  </div>
                  <div className="divide-y divide-blue-100">
                    {/* Email */}
                    {selectedContact.email ? (
                      <a href={`mailto:${selectedContact.email}`} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors">
                        <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Email</span>
                        <span className="font-semibold text-slate-800 truncate">{selectedContact.email}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Email</span>
                        <span className="text-slate-400">Non renseigné</span>
                      </div>
                    )}
                    {/* Téléphone */}
                    {selectedContact.phone ? (
                      <a href={`tel:${selectedContact.phone}`} className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors">
                        <Phone className="w-4 h-4 text-blue-500 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Téléphone</span>
                        <span className="font-semibold text-slate-800">{selectedContact.phone}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3 px-4 py-3">
                        <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                        <span className="text-xs text-slate-400 w-20 shrink-0">Téléphone</span>
                        <span className="text-slate-400">Non renseigné</span>
                      </div>
                    )}
                    {/* LinkedIn (URL enregistrée OU recherche pré-remplie) */}
                    {selectedContact.linkedin_url ? (
                      <a
                        href={selectedContact.linkedin_url.startsWith('http') ? selectedContact.linkedin_url : `https://${selectedContact.linkedin_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors"
                      >
                        <svg viewBox="0 0 24 24" fill="#0A66C2" className="w-4 h-4 shrink-0"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
                        <span className="text-xs text-slate-400 w-20 shrink-0">LinkedIn</span>
                        <span className="font-semibold truncate flex-1" style={{ color: '#0A66C2' }}>Voir le profil</span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      </a>
                    ) : (
                      <a
                        href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent([selectedContact.first_name, selectedContact.last_name, selectedContact.company_name].filter(Boolean).join(' '))}&origin=GLOBAL_SEARCH_HEADER`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-blue-100/40 transition-colors"
                        title="Ouvrir une recherche LinkedIn pré-remplie"
                      >
                        <svg viewBox="0 0 24 24" fill="#0A66C2" className="w-4 h-4 shrink-0"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"/></svg>
                        <span className="text-xs text-slate-400 w-20 shrink-0">LinkedIn</span>
                        <span className="font-semibold flex-1" style={{ color: '#0A66C2' }}>Rechercher sur LinkedIn</span>
                        <ExternalLink className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      </a>
                    )}
                  </div>
                </div>

                {/* Zone Entreprise (indigo) */}
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-indigo-100/70 border-b border-indigo-100">
                    <Building2 className="w-4 h-4 text-indigo-700" />
                    <h3 className="text-xs font-bold text-indigo-800 uppercase tracking-wide">Entreprise</h3>
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    <span className="block font-semibold text-slate-800">
                      {selectedContact.company_name || 'Non renseignée'}
                    </span>
                    <LienProjetsDeLEntreprise companyId={selectedContact.company_id} suivi={suiviEntreprise} />
                  </div>
                </div>

                {/* Zone Accès UP-SPOT (violet) — UP-CRM est la porte d'entrée des comptes */}
                <UpSpotPanel
                  contactId={selectedContact.id}
                  email={selectedContact.email}
                  onChange={() => void loadContacts()}
                />

                {/* Zone Champs personnalisés (ambre) */}
                <div className="rounded-2xl border border-amber-100 bg-amber-50/50 overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-100/70 border-b border-amber-100">
                    <SlidersHorizontal className="w-4 h-4 text-amber-700" />
                    <h3 className="text-xs font-bold text-amber-800 uppercase tracking-wide">Champs personnalisés</h3>
                  </div>
                  {loadingContactDetails ? (
                    <div className="divide-y divide-amber-100">
                      {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="flex items-center gap-3 px-4 py-3">
                          <div className="h-3 bg-amber-200/60 rounded animate-pulse w-24"></div>
                          <div className="h-4 bg-amber-200/60 rounded animate-pulse w-32"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      {/* Affichage depuis custom_fields (API détaillée) */}
                      {selectedContact.custom_fields && selectedContact.custom_fields.filter((c) => c.value).length > 0 ? (
                        <div className="divide-y divide-amber-100">
                          {selectedContact.custom_fields.map((customFieldData) => {
                            if (!customFieldData.value) return null

                            return (
                              <div key={customFieldData.field.id} className="flex items-center gap-3 px-4 py-3">
                                <span className="text-xs text-slate-400 w-28 shrink-0">{customFieldData.field.name}</span>
                                <span className="font-semibold text-slate-800">{customFieldData.value}</span>
                              </div>
                            )
                          })}
                        </div>
                      ) : (
                        // Fallback sur custom_field_values (données du tableau)
                        selectedContact.custom_field_values && Object.keys(selectedContact.custom_field_values).length > 0 ? (
                          <div className="divide-y divide-amber-100">
                            {customFields.map((customField) => {
                              const fieldValue = selectedContact.custom_field_values?.[customField.id]
                              if (!fieldValue) return null

                              return (
                                <div key={customField.id} className="flex items-center gap-3 px-4 py-3">
                                  <span className="text-xs text-slate-400 w-28 shrink-0">{customField.name}</span>
                                  <span className="font-semibold text-slate-800">{fieldValue}</span>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 px-4 py-3">Aucun champ personnalisé renseigné</p>
                        )
                      )}
                    </>
                  )}
                </div>

                {/* Action Fermer */}
                <Button
                  variant="outline"
                  className="w-full font-semibold"
                  onClick={() => setIsDetailModalOpen(false)}
                >
                  Fermer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {fusion && (
        <FusionDialog
          type="contact"
          open
          ficheId={fusion.ficheId}
          autreId={fusion.autreId}
          onClose={() => setFusion(null)}
          onFusion={(gardeId) => {
            setFusion(null)
            void loadContacts()
            void ouvrirContact(gardeId)
          }}
        />
      )}

      {/* Modal de confirmation de suppression */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className={`${contactToDelete?.upspot_compte_id ? 'sm:max-w-[540px]' : 'sm:max-w-[425px]'} rounded-2xl sm:rounded-2xl`}>
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              <span>Confirmer la suppression</span>
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce contact ? Il ira dans la corbeille, d'où un administrateur pourra le restaurer.
            </DialogDescription>
          </DialogHeader>
          
          {contactToDelete && (
            <div className="py-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {contactToDelete.first_name} {contactToDelete.last_name}
                </p>
                {contactToDelete.email && (
                  <p className="text-sm text-gray-600">{contactToDelete.email}</p>
                )}
                {contactToDelete.phone && (
                  <p className="text-sm text-gray-600">{contactToDelete.phone}</p>
                )}
              </div>
              {contactToDelete.upspot_compte_id && (
                <div className="mt-3">
                  <ChoixSuppressionUpSpot
                    contactId={contactToDelete.id}
                    choix={choixUpSpot}
                    onChoix={setChoixUpSpot}
                    confirme={suppressionConfirmee}
                    onConfirme={setSuppressionConfirmee}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={cancelDeleteContact}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteContact}
              disabled={
                isDeleting ||
                Boolean(
                  contactToDelete?.upspot_compte_id &&
                    !suppressionPossible(estAdminCrm, choixUpSpot, suppressionConfirmee),
                )
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Mettre à la corbeille
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}